import { useState } from 'react';
import { RefreshCw, RotateCcw } from 'lucide-react';
import { useTranslation } from '../../../i18n';
import { Card } from '../../../components/Shared/Card';
import { Button } from '../../../components/Shared/Button';
import { ConfirmDialog } from '../../../components/Shared/ConfirmDialog';
import { useAdminDemoDataStore, type DemoDataAction } from '../../../store/useAdminDemoDataStore';

const ACTIONS: { action: DemoDataAction; icon: typeof RefreshCw; variant: 'warning' | 'danger' }[] = [
  { action: 'refresh', icon: RefreshCw, variant: 'warning' },
  { action: 'reset', icon: RotateCcw, variant: 'danger' },
];

export default function DemoDataAdminPage() {
  const { t } = useTranslation(['admin', 'common']);
  const { running, run } = useAdminDemoDataStore();
  const [confirm, setConfirm] = useState<DemoDataAction | null>(null);
  const confirmVariant = ACTIONS.find((a) => a.action === confirm)?.variant;

  const handleConfirm = async () => {
    if (!confirm) return;
    await run(confirm);
    setConfirm(null);
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <h1 className="text-[22px] sm:text-[24px] font-semibold tracking-tight text-slate-900">
          {t('admin:demoData.title')}
        </h1>
        <p className="text-[13px] text-slate-500 mt-0.5">{t('admin:demoData.subtitle')}</p>
      </div>

      {ACTIONS.map(({ action, icon: Icon, variant }) => (
        <Card key={action} title={t(`admin:demoData.${action}.title`)}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-[13px] text-slate-700 max-w-2xl">
              {t(`admin:demoData.${action}.description`)}
            </p>
            <Button
              variant={variant === 'danger' ? 'danger' : 'secondary'}
              leftIcon={<Icon className="w-3.5 h-3.5" strokeWidth={2} />}
              loading={running === action}
              disabled={running !== null}
              onClick={() => setConfirm(action)}
            >
              {t(`admin:demoData.${action}.button`)}
            </Button>
          </div>
        </Card>
      ))}

      <ConfirmDialog
        isOpen={confirm !== null}
        onClose={() => setConfirm(null)}
        onConfirm={handleConfirm}
        title={confirm ? t(`admin:demoData.${confirm}.confirmTitle`) : ''}
        message={confirm ? t(`admin:demoData.${confirm}.confirmMessage`) : ''}
        confirmText={confirm ? t(`admin:demoData.${confirm}.button`) : undefined}
        variant={confirmVariant}
        loading={running !== null}
      />
    </div>
  );
}
